import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  LanguageIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  UserGroupIcon,
  PlayCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

// Reusable responsive Vimeo player
const VimeoEmbed = ({ id, title }) => (
  <div className="relative w-full overflow-hidden rounded-2xl bg-black shadow-lg aspect-video">
    <iframe
      src={`https://player.vimeo.com/video/${id}`}
      title={title}
      className="absolute inset-0 w-full h-full"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
    />
  </div>
);

// What each program is (general NYC Public Schools definitions)
const programTypes = [
  {
    icon: LanguageIcon,
    title: 'Dual Language (DL)',
    color: 'bg-blue-600',
    description:
      'Students learn in two languages — English and a partner language such as Spanish. The goal is full bilingualism and biliteracy, with instructional time shared between both languages.',
  },
  {
    icon: AcademicCapIcon,
    title: 'Transitional Bilingual Education (TBE)',
    color: 'bg-green-600',
    description:
      'Students receive instruction in their home language and in English. Over time, instruction gradually shifts toward English as students build their English proficiency.',
  },
  {
    icon: GlobeAltIcon,
    title: 'English as a New Language (ENL)',
    color: 'bg-secondary',
    description:
      'Formerly known as ESL, ENL provides instruction primarily in English with targeted support, so multilingual learners build English language skills while learning grade-level content.',
  },
];

// Two-language overview of DL & TBE programs in NYS
const overviewVideos = [
  { lang: 'English', id: '356461427' },
  { lang: 'Spanish', id: '356461345' },
];

// Parent orientation video, available in multiple languages
const orientationVideos = [
  { lang: 'English', id: '158544178' },
  { lang: 'Spanish', id: '158911172' },
  { lang: 'French', id: '158911167' },
  { lang: 'Arabic', id: '169152988' },
  { lang: 'Russian', id: '162881006' },
  { lang: 'Bengali', id: '169152988' },
  { lang: 'Haitian Creole', id: '158911168' },
];

const DualLanguageProgramPage = () => {
  const [activeLang, setActiveLang] = useState(orientationVideos[0]);

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
              <LanguageIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Dual Language &amp; <span className="text-secondary">ENL Programs</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
              Learn how our multilingual programs help every student grow as a confident,
              bilingual learner.
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            CHOOSING THE RIGHT PROGRAM
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-primary"></div>
              <span className="text-orange-700 font-bold tracking-widest text-sm uppercase mb-2 block">
                For Families
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-primary tracking-tight mb-6">
                Choosing the Right <span className="font-semibold">Program</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                Every multilingual learner is unique. New York City Public Schools offers several
                program options to support students who are learning English. The videos and
                resources below explain each program so you can make the best choice for your child.
              </p>
              <a
                href="https://www.schools.nyc.gov/learning/multilingual-learners/english-language-learners"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-bold text-white bg-primary rounded-full hover:bg-navy-700 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                Explore NYC Multilingual Learner Resources
                <ArrowTopRightOnSquareIcon className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        {/* ============================================================================
            PROGRAM TYPES
        ============================================================================ */}
        <section className="pb-16 md:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-primary tracking-tight">
                Understanding the <span className="font-semibold">Program Types</span>
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                A quick look at the multilingual program models offered across New York City Public Schools.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {programTypes.map((program) => (
                <div
                  key={program.title}
                  className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden flex flex-col"
                >
                  <div className={`${program.color} p-6 text-white`}>
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm mb-4">
                      <program.icon className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold leading-snug">{program.title}</h3>
                  </div>
                  <div className="p-6 flex-1">
                    <p className="text-gray-700 leading-relaxed">{program.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================================
            DL & TBE OVERVIEW VIDEOS (English / Spanish)
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-primary">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-accent-yellow font-extrabold tracking-[0.2em] text-sm uppercase mb-3 block">
                Watch &amp; Learn
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                Dual Language &amp; Bilingual Programs <span className="font-semibold">in New York State</span>
              </h2>
              <p className="mt-4 text-lg text-white/90 max-w-2xl mx-auto">
                An overview of Dual Language and Transitional Bilingual Education (TBE) programs.
                Available in English and Spanish.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              {overviewVideos.map((video) => (
                <div key={video.lang}>
                  <div className="flex items-center gap-2 mb-4">
                    <PlayCircleIcon className="w-6 h-6 text-secondary" />
                    <h3 className="text-xl font-extrabold text-white">{video.lang}</h3>
                  </div>
                  <VimeoEmbed
                    id={video.id}
                    title={`Dual Language & TBE Programs in NYS — ${video.lang}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================================
            PARENT ORIENTATION (multi-language)
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-6">
                <UserGroupIcon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-primary tracking-tight">
                Parent Orientation <span className="font-semibold">Video</span>
              </h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                This orientation explains the Dual Language, TBE, and English Language Learner
                programs. Choose your language below to watch.
              </p>
            </div>

            {/* Language selector */}
            <div className="flex flex-wrap justify-center gap-3 mb-8" role="tablist" aria-label="Select video language">
              {orientationVideos.map((video) => {
                const isActive = activeLang.lang === video.lang;
                return (
                  <button
                    key={video.lang}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveLang(video)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 ${
                      isActive
                        ? 'bg-orange-600 text-white shadow-md'
                        : 'bg-white text-navy-700 border border-gray-200 hover:border-secondary hover:text-orange-700'
                    }`}
                  >
                    {video.lang}
                  </button>
                );
              })}
            </div>

            <VimeoEmbed
              key={activeLang.id}
              id={activeLang.id}
              title={`Parent Orientation Video — ${activeLang.lang}`}
            />
            <p className="text-center text-sm text-gray-700 mt-4">
              Now playing: Parent Orientation in <span className="font-bold text-primary">{activeLang.lang}</span>
            </p>
          </div>
        </section>

        {/* ============================================================================
            CTA SECTION
        ============================================================================ */}
        <section className="py-20 md:py-24 bg-accent-yellow">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-12 md:p-16 shadow-2xl text-center relative overflow-hidden bg-primary">
              <LionsStripe className="absolute top-0 inset-x-0" />
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-6">Have Questions About Our Programs?</h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Our team is here to help you understand your options and find the right fit for your
                child. Reach out and we'll be glad to talk with you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-extrabold text-primary bg-accent-yellow rounded-full hover:bg-white shadow-lg hover:shadow-xl hover:-translate-y-1 motion-reduce:transform-none transition-all duration-300"
                >
                  Contact Us
                </a>
                <a
                  href="/learning-resources"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white border-2 border-white/50 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  Learning Resources
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DualLanguageProgramPage;
